import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  User, LogOut, Store, ChevronDown, LayoutDashboard,
  CreditCard, Shield
} from 'lucide-react';

export function UserMenu({ showOutletSwitch = true, compact = false }) {
  const navigate = useNavigate();
  const { user, logout, currentOutlet, isPlatformAdmin, isOwner, isManager } = useAuth();

  const handleLogout = () => { logout(); navigate('/login'); };
  const handleSwitchOutlet = () => navigate('/select-outlet');
  const handleGoToDashboard = () => navigate('/dashboard');
  const handleGoToPOS = () => navigate('/pos');
  const handleGoToAdmin = () => navigate('/platform/admin');

  const getRoleBadgeColor = () => {
    if (isPlatformAdmin) return 'bg-primary';
    if (isOwner) return 'bg-primary';
    if (isManager) return 'bg-secondary';
    return 'bg-muted-foreground';
  };

  const getRoleLabel = () => {
    if (isPlatformAdmin) return 'Platform Admin';
    return user?.role_name?.replace('_', ' ') || 'User';
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-2 text-foreground hover:bg-accent" data-testid="user-menu-trigger">
          <div className="w-9 h-9 rounded-full bg-secondary/15 flex items-center justify-center">
            <User className="w-4 h-4 text-secondary" />
          </div>
          {!compact && (
            <>
              <div className="text-left hidden sm:block">
                <p className="text-sm font-semibold leading-tight">{user?.name || user?.email}</p>
                <p className="text-xs text-muted-foreground">{currentOutlet?.name || getRoleLabel()}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex items-center gap-2 text-muted-foreground font-normal text-sm">
            <span className="truncate">{user?.email}</span>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded mt-1 inline-block text-white capitalize ${getRoleBadgeColor()}`}>
            {getRoleLabel()}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {currentOutlet && (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">Current Outlet</DropdownMenuLabel>
            <DropdownMenuItem disabled>
              <Store className="w-4 h-4 mr-2 text-secondary" />
              {currentOutlet.name}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {(isOwner || isManager) && currentOutlet && (
          <>
            <DropdownMenuItem onClick={handleGoToDashboard} className="cursor-pointer" data-testid="menu-dashboard">
              <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleGoToPOS} className="cursor-pointer" data-testid="menu-pos">
              <CreditCard className="w-4 h-4 mr-2" /> Point of Sale
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {isPlatformAdmin && (
          <DropdownMenuItem onClick={handleGoToAdmin} className="cursor-pointer" data-testid="menu-admin">
            <Shield className="w-4 h-4 mr-2" /> Platform Admin
          </DropdownMenuItem>
        )}

        {showOutletSwitch && !isPlatformAdmin && (
          <DropdownMenuItem onClick={handleSwitchOutlet} className="cursor-pointer" data-testid="menu-switch-outlet">
            <Store className="w-4 h-4 mr-2" /> Switch Outlet
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive cursor-pointer" data-testid="menu-logout">
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default UserMenu;
